import React, { useState, useEffect, useRef } from 'react';
import {
    FiPlus, FiTrash2, FiEdit2, FiImage, FiExternalLink,
    FiCheckCircle, FiXCircle, FiLayers, FiEye, FiSettings, FiX, FiAlertTriangle
} from 'react-icons/fi';
import { sliderService } from '../../../services/api';
import './AdminSlider.css';

const AdminSlider = () => {
    const [sliders, setSliders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [sliderToDelete, setSliderToDelete] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Form State
    const [form, setForm] = useState({
        title: '',
        subtitle: '',
        image: '',
        buttonText: 'Shop Now',
        buttonLink: '/products',
        order: 1,
        isActive: true
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const data = await sliderService.getSliders();
            setSliders(data);
        } catch (err) {
            console.error('Failed to fetch:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Auto-dismiss feedback
    useEffect(() => {
        if (errorMsg || successMsg) {
            const timer = setTimeout(() => {
                setErrorMsg('');
                setSuccessMsg('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [errorMsg, successMsg]);

    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 700; // Scaled down for 413 safety
                    const scaleSize = MAX_WIDTH / img.width;
                    if (img.width > MAX_WIDTH) {
                        canvas.width = MAX_WIDTH;
                        canvas.height = img.height * scaleSize;
                    } else {
                        canvas.width = img.width;
                        canvas.height = img.height;
                    }
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/webp', 0.7)); // Changed to webp to preserve transparency
                };
            };
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // 1. Instant Compression (Reduced to 900px for speed and 413 safety)
            const compressedBase64 = await compressImage(file);
            const rawBase64 = compressedBase64.split(',')[1];

            setImagePreview({ url: compressedBase64, source: 'local' });
            setIsUploadingImage(true);

            let uploadUrl = null;

            // 2. Focused Upload (Prioritize Telegra.ph - No Key Required)
            const uploadToTelegraph = async (base64) => {
                try {
                    const blob = await (await fetch(base64)).blob();
                    const fd = new FormData();
                    fd.append('file', blob);
                    const res = await fetch('https://telegra.ph/upload', {
                        method: 'POST', body: fd,
                        signal: AbortSignal.timeout(10000)
                    });
                    const data = await res.json();
                    if (data?.[0]?.src) {
                        const src = data[0].src;
                        const finalUrl = src.startsWith('/') ? `https://telegra.ph${src}` : `https://telegra.ph/${src}`;
                        console.log("Banner Cloud Link Generated:", finalUrl);
                        return finalUrl;
                    }
                    return null;
                } catch (e) { return null; }
            };

            const uploadToImgBB = async (raw) => {
                try {
                    const fd = new FormData();
                    fd.append('key', 'a605aeec82026edda40d3c76675cdb9e');
                    const blob = await (await fetch(raw)).blob();
                    fd.append('image', blob, 'upload.jpg');
                    const res = await fetch('https://api.imgbb.com/1/upload', {
                        method: 'POST', body: fd,
                        signal: AbortSignal.timeout(10000)
                    });
                    const data = await res.json();
                    if (data?.success) return data.data.url;
                    return null;
                } catch (e) { return null; }
            };

            uploadUrl = await uploadToTelegraph(compressedBase64);
            if (!uploadUrl) {
                uploadUrl = await uploadToImgBB(rawBase64);
            }

            if (uploadUrl) {
                setForm(prev => ({ ...prev, image: uploadUrl }));
                setImagePreview({ url: uploadUrl, source: 'cloud' });
                setSuccessMsg('Image synced to cloud');
            } else {
                // ULTRA-SHRINK LOCAL FALLBACK (Solves 413 Payload Error)
                const ultraShrink = await new Promise(resolve => {
                    const i = new Image();
                    i.src = compressedBase64;
                    i.onload = () => {
                        const c = document.createElement('canvas');
                        c.width = 400; // Extremely small width to pass node body limit
                        c.height = i.height * (400 / i.width);
                        c.getContext('2d').drawImage(i, 0, 0, c.width, c.height);
                        resolve(c.toDataURL('image/webp', 0.5)); // Preserves transparency
                    };
                });

                if (ultraShrink.length > 300000) {
                    setErrorMsg('Image is still too large to save. Try using an image with less detail.');
                    setImagePreview(null);
                    return;
                }
                setForm(prev => ({ ...prev, image: ultraShrink }));
                setImagePreview({ url: ultraShrink, source: 'local' });
                setSuccessMsg('Local asset ready');
            }
        } catch (err) {
            setErrorMsg('System could not process this image.');
        } finally {
            setIsUploadingImage(false);
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        setForm(prev => ({ ...prev, image: '' }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditClick = (s) => {
        const sId = s._id || s.id;
        setForm({
            title: s.title,
            subtitle: s.subtitle,
            image: s.image,
            buttonText: s.buttonText,
            buttonLink: s.buttonLink,
            order: s.order,
            isActive: s.isActive
        });
        setImagePreview(s.image ? { url: s.image, source: 'cloud' } : null);
        setEditId(sId);
        setIsEditMode(true);
        setShowDrawer(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMsg('');
        setSuccessMsg('');

        try {
            if (isEditMode) {
                const updated = await sliderService.updateSlider(editId, form);
                setSliders(prev => prev.map(s => s.id === editId ? updated : s).sort((a, b) => a.order - b.order));
                setSuccessMsg('Banner updated successfully');
            } else {
                const newSlider = await sliderService.createSlider(form);
                setSliders(prev => [...prev, newSlider].sort((a, b) => a.order - b.order));
                setSuccessMsg('New banner added successfully');
            }
            setShowDrawer(false);
            resetForm();
        } catch (err) {
            setErrorMsg(err.message || 'Saving failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setIsEditMode(false);
        setEditId(null);
        setImagePreview(null);
        setForm({
            title: '', subtitle: '', image: '',
            buttonText: 'Shop Now', buttonLink: '/products',
            order: sliders.length + 1, isActive: true
        });
    };

    const initiateDelete = (id) => {
        setSliderToDelete(id);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!sliderToDelete) return;
        setIsDeleting(true);
        try {
            await sliderService.deleteSlider(sliderToDelete);
            setSliders(prev => prev.filter(s => (s._id || s.id) !== sliderToDelete));
            setShowConfirmModal(false);
            setSuccessMsg('Banner deleted successfully');
            setSliderToDelete(null);
        } catch (err) {
            setErrorMsg('Failed to delete banner. Please check connection.');
            console.error('Delete Error:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="admin-slider-page">
            {errorMsg && (
                <div className="adr-error-pop">
                    <div className="adr-error-icon-box">
                        <FiX />
                    </div>
                    <div className="adr-error-text">
                        {errorMsg}
                    </div>
                </div>
            )}
            {successMsg && (
                <div className="adr-error-pop success">
                    <div className="adr-error-icon-box">
                        <FiCheckCircle />
                    </div>
                    <div className="adr-error-text">
                        {successMsg}
                    </div>
                </div>
            )}

            <div className="slider-premium-banner">
                <div className="banner-glow-effect"></div>
                <div className="banner-content">
                    <div className="banner-tag">STORE BANNERS</div>
                    <h1 className="banner-title">Banner <span>Slider</span></h1>
                    <p className="banner-subtitle">
                        Add and manage banners for your store's home page.
                    </p>
                    <div className="banner-actions">
                        <button className="btn-create-slider" onClick={() => setShowDrawer(true)}>
                            <FiPlus /> Add New Banner
                        </button>
                    </div>
                </div>
            </div>

            <div className="sliders-management-container">
                <div className="section-header">
                    <h2>Banner List <span>({sliders.length} Banners)</span></h2>
                </div>

                {isLoading ? (
                    <div className="sliders-skeleton-grid">
                        {[1, 2, 3].map(v => <div key={v} className="skeleton-card"></div>)}
                    </div>
                ) : (
                    <div className="sliders-grid">
                        {sliders.map((slider) => {
                            const sId = slider._id || slider.id;
                            return (
                                <div key={sId} className={`slider-management-card ${!slider.isActive ? 'inactive' : ''}`}>
                                    <div className="card-image-wrapper">
                                        <img src={slider.image || 'https://placehold.co/800x400?text=No+Image'} alt={slider.title} />
                                        <div className={`status-badge ${slider.isActive ? 'active' : 'hidden'}`}>
                                            {slider.isActive ? <FiCheckCircle /> : <FiXCircle />}
                                            <span>{slider.isActive ? 'Visible' : 'Hidden'}</span>
                                        </div>
                                        <div className="order-badge">#{slider.order}</div>
                                    </div>

                                    <div className="card-details">
                                        <div className="detail-row">
                                            <h3 className="slider-title">{slider.title}</h3>
                                            <p className="slider-subtitle">{slider.subtitle}</p>
                                        </div>

                                        <div className="detail-meta">
                                            <span className="btn-preview-meta"><FiExternalLink /> {slider.buttonText}</span>
                                            <span className="link-preview-meta">{slider.buttonLink}</span>
                                        </div>

                                        <div className="card-actions">
                                            <button className="btn-action-edit" onClick={() => handleEditClick(slider)}><FiEdit2 /></button>
                                            <button className="btn-action-delete" onClick={() => initiateDelete(sId)}>
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {sliders.length === 0 && (
                            <div className="empty-gallery-state">
                                <FiImage className="empty-icon" />
                                <p>The hero gallery is currently empty.</p>
                                <button onClick={() => setShowDrawer(true)}>Initial Deployment</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Creation Side Drawer */}
            <div className={`slider-drawer-overlay ${showDrawer ? 'active' : ''}`} onClick={() => !isSubmitting && setShowDrawer(false)}>
                <div className="slider-side-drawer" onClick={e => e.stopPropagation()}>
                    <header className="drawer-header">
                        <h2>{isEditMode ? 'Edit Banner' : 'Add New Banner'}</h2>
                        <button className="btn-close-drawer" onClick={() => { setShowDrawer(false); resetForm(); }}><FiX /></button>
                    </header>

                    <div className="drawer-content">
                        {/* Live Visual Preview */}
                        <div className="live-preview-section">
                            <label className="preview-label">Live Preview</label>
                            <div className="preview-bubble">
                                <div className="mini-slide">
                                    <img src={form.image || 'https://placehold.co/800x400?text=Visual+Preview'} alt="Preview" />
                                    <div className="mini-content">
                                        <h4>{form.title || 'Winter Sale'}</h4>
                                        <p>{form.subtitle || 'Premium Collection'}</p>
                                        <button disabled>{form.buttonText}</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="slider-form">
                            <div className="form-group">
                                <label>Banner Title</label>
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleInputChange}
                                    placeholder="Enter headline..."
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Subtitle</label>
                                <input
                                    name="subtitle"
                                    value={form.subtitle}
                                    onChange={handleInputChange}
                                    placeholder="Add sub-text..."
                                />
                            </div>

                            <div className="form-group">
                                <label>Banner Image</label>
                                <div className="image-upload-wrapper same-to-same">
                                    {!imagePreview ? (
                                        <div className="upload-box" onClick={() => fileInputRef.current.click()}>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                            />
                                            <FiPlus className="plus-icon" />
                                            <span>Photos</span>
                                        </div>
                                    ) : (
                                        <div className="image-previews-grid">
                                            <div className={`preview-card ${isUploadingImage ? 'processing' : ''}`}>
                                                <img src={imagePreview.url} alt="Slide Preview" />
                                                <button type="button" className="remove-btn" onClick={removeImage}>
                                                    <FiX />
                                                </button>
                                                {isUploadingImage && <div className="mini-loader-overlay">Syncing...</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Button Text</label>
                                    <input name="buttonText" value={form.buttonText} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>Link</label>
                                    <input name="buttonLink" value={form.buttonLink} onChange={handleInputChange} />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Order</label>
                                    <input type="number" name="order" value={form.order} onChange={handleInputChange} />
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>Active</label>
                                    <div className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            id="isActiveToggle"
                                            checked={form.isActive}
                                            onChange={handleInputChange}
                                        />
                                        <label htmlFor="isActiveToggle"></label>
                                    </div>
                                </div>
                            </div>

                            <div className="drawer-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowDrawer(false); resetForm(); }}>Discard</button>
                                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Banner'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Confirm Deletion Modal */}
            {showConfirmModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-card">
                        <div className="modal-icon-ring warning">
                            <FiAlertTriangle />
                        </div>
                        <h3>Delete Banner?</h3>
                        <p>Are you sure you want to delete this banner? This action cannot be undone.</p>
                        <div className="modal-btn-group">
                            <button
                                className="modal-btn cancel"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal-btn confirm-delete"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Banner'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSlider;

